import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface ApiKey {
  id: string;
  name: string;
  createdAt: string;
  expiresAt: string | null;
  key: string;
  status: 'active' | 'expired' | 'revoked';
  lastUsed?: string;
}

interface ApiKeyContextType {
  apiKeys: ApiKey[];
  isLoading: boolean;
  createApiKey: (name: string) => Promise<ApiKey>;
  revokeApiKey: (id: string) => Promise<void>;
  getApiKeyById: (id: string) => ApiKey | undefined;
  fetchApiKeys: () => Promise<void>;
}

const ApiKeyContext = createContext<ApiKeyContextType | undefined>(undefined);

export function ApiKeyProvider({ children }: { children: ReactNode }) {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get authentication status
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchApiKeys = async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.get('/auth/api-keys');
      if (response && response.apiKeys) {
        setApiKeys(response.apiKeys);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
      
      // Fallback to localStorage if API fails
      try {
        const storedKeys = localStorage.getItem('apiKeys');
        if (storedKeys) {
          setApiKeys(JSON.parse(storedKeys));
        }
      } catch (localError) {
        console.error('Failed to load API keys from localStorage:', localError);
      }
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    // Only fetch data when authentication is resolved and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchApiKeys();
    } else if (!authLoading && !isAuthenticated) {
      // Not authenticated, set loading to false
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  // Still keep localStorage as a backup
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('apiKeys', JSON.stringify(apiKeys));
    }
  }, [apiKeys, isLoading]);

  const createApiKey = async (name: string): Promise<ApiKey> => {
    try {
      // Call the API key creation endpoint
      const response = await api.post('/auth/api-keys', { name });
      
      logger.log("API key creation response:", response);
      
      if (response && response.apiKey) {
        const newApiKey = response.apiKey;
        
        // Set the new API key as the current API key for the API client
        api.setApiKey(newApiKey.key);
        logger.log("Setting new API key for future requests:", newApiKey.key);
        
        setApiKeys(prevKeys => [...prevKeys, newApiKey]);
        toast.success(`API key "${name}" created successfully`);
        return newApiKey;
      } else {
        console.error("Invalid API response format:", response);
        throw new Error('Invalid API response format');
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
      throw error;
    }
  };

  const revokeApiKey = async (id: string) => {
    try {
      // In a real implementation, this would call the API
      await api.post(`/auth/api-keys/${id}/revoke`);
      
    setApiKeys(prevKeys => 
      prevKeys.map(key => 
        key.id === id ? { ...key, status: 'revoked' as const } : key
      )
    );
    toast.success('API key revoked successfully');
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error('Failed to revoke API key');
      throw error;
    }
  };

  const getApiKeyById = (id: string) => {
    return apiKeys.find(key => key.id === id);
  };

  return (
    <ApiKeyContext.Provider value={{ 
      apiKeys, 
      isLoading, 
      createApiKey, 
      revokeApiKey, 
      getApiKeyById,
      fetchApiKeys 
    }}>
      {children}
    </ApiKeyContext.Provider>
  );
}

export function useApiKeys() {
  const context = useContext(ApiKeyContext);
  if (context === undefined) {
    throw new Error('useApiKeys must be used within an ApiKeyProvider');
  }
  return context;
}
